import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericApiService } from '@pm-ui/core-services';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// ── Request / Response DTOs ────────────────────────────────────────────────

export interface TranslationRequestDto {
  /** HTML or plain-text to translate to English. */
  content: string;
  /** Optional hint for logging (e.g. "TaskDescription", "ActionTaken"). */
  fieldHint?: string;
}

export interface TranslationResponseDto {
  /** The translated (or original, if already English) content. */
  translatedContent: string;
  /** Language detected in the input (e.g. "English", "French", "Hindi"). */
  detectedLanguage: string;
  /** True when no translation was needed. */
  wasAlreadyEnglish: boolean;
  /** Azure OpenAI input token count. */
  inputTokens: number;
  /** Azure OpenAI output token count. */
  outputTokens: number;
  /** Azure OpenAI total token count (input + output). */
  totalTokens: number;
  /** True if the AI call failed and original content was returned unchanged. */
  fallbackUsed: boolean;
  /** Error message when fallbackUsed is true. Empty string on success. */
  errorMessage: string;
}

// ── Endpoints ──────────────────────────────────────────────────────────────

const TRANSLATE_ENDPOINT = 'api/Translation/Translate';
const PREVIEW_ENDPOINT   = 'api/Translation/Preview';

// ── Service ────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  constructor(
    private genericApiService: GenericApiService,
    private http: HttpClient
  ) {}

  /**
   * Translates content to English via the backend AI service.
   * Returns the full TranslationResponseDto including token-usage metrics.
   */
  translate(content: string, fieldHint: string = ''): Observable<TranslationResponseDto> {
    const reqModel: TranslationRequestDto = { content, fieldHint };
    return this.genericApiService
      .post<TranslationResponseDto>(TRANSLATE_ENDPOINT, reqModel)
      .pipe(
        map((res: HttpResponse<TranslationResponseDto>) => {
          const body = res.body as TranslationResponseDto;
          if (body) {
            // Log token usage to console (not shown in UI by default)
            console.log(
              `[Translation] Field=${fieldHint || 'N/A'} | Language=${body.detectedLanguage} | ` +
              `AlreadyEnglish=${body.wasAlreadyEnglish} | ` +
              `Tokens — Input: ${body.inputTokens}, Output: ${body.outputTokens}, Total: ${body.totalTokens}` +
              (body.fallbackUsed ? ` | ⚠ FALLBACK: ${body.errorMessage}` : '')
            );
          }
          return body;
        })
      );
  }

  /**
   * Preview endpoint — returns both the original and translated text.
   * Useful for Swagger testing and diagnostic tooling.
   */
  preview(content: string, fieldHint: string = ''): Observable<any> {
    const reqModel: TranslationRequestDto = { content, fieldHint };
    return this.genericApiService
      .post<any>(PREVIEW_ENDPOINT, reqModel)
      .pipe(map((res: HttpResponse<any>) => res.body));
  }
}
